import { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import ConfirmationDialog from '~/components/Dialogs/ConfirmationDialog';
import { Roi } from '~/domain/dicom/dicomstructureset';
import { environmentState, mainViewState } from '~/state/atoms/engineAtoms';
import {
    openedStructureDeletionConfirmationDialogState,
    selectedROIState,
} from '~/state/atoms/structureSet/roiAtoms';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';
import { selectedToolState } from '~/state/atoms/toolsAtoms';

const StructureDeletionConfirmationDialog = ({
    roi,
    isSelected,
    onDelete,
}: {
    roi: Roi;
    isSelected: boolean;
    onDelete: (roiNumber: number, sopUid: string) => void;
}) => {
    const currentStructureSet = useRecoilValue(currentStructureSetState);
    const mainView = useRecoilValue(mainViewState);
    const environment = useRecoilValue(environmentState);
    const selectedTool = useRecoilValue(selectedToolState);
    const setSelectedROI = useSetRecoilState<number>(selectedROIState);
    const [
        openedStructureDeletionConfirmationDialog,
        setOpenedStructureDeletionConfirmationDialog,
    ] = useRecoilState(openedStructureDeletionConfirmationDialogState);

    useEffect(() => {
        const handleRoiDeletionKeyDown = (event: KeyboardEvent) => {
            if (
                event.key === 'Enter' &&
                openedStructureDeletionConfirmationDialog === roi.roiNumber
            ) {
                handleDeleteStructureConfirmation();
            }
        };
        document.addEventListener('keydown', handleRoiDeletionKeyDown);
        return () => {
            document.removeEventListener('keydown', handleRoiDeletionKeyDown);
        };
    }, [openedStructureDeletionConfirmationDialog === roi.roiNumber]);

    useEffect(() => {
        const handleRoiDeletionKeyDown = (event: KeyboardEvent) => {
            const eventSourceElement = event.target as HTMLElement;
            const isEditableElement =
                eventSourceElement.tagName === 'INPUT' ||
                eventSourceElement.tagName === 'TEXTAREA' ||
                (eventSourceElement as HTMLElement).isContentEditable;

            if (isEditableElement) {
                return;
            }
            if (event.key === 'Delete' && isSelected) {
                if (selectedTool === 'brush' || selectedTool === 'line') {
                    quickApplyClearContoursOnSliceOperation();
                } else {
                    if (event.shiftKey) {
                        quickApplyClearContoursOnSliceOperation();
                    } else {
                        handleDeleteStructureDialogOpen();
                    }
                }
            }
        };

        document.addEventListener('keydown', handleRoiDeletionKeyDown);
        return () => {
            document.removeEventListener('keydown', handleRoiDeletionKeyDown);
        };
    }, [isSelected, selectedTool]);

    const handleDeleteStructureDialogOpen = () => {
        setOpenedStructureDeletionConfirmationDialog(roi.roiNumber);
    };

    const handleDeleteStructureConfirmation = () => {
        if (isSelected) {
            setSelectedROI(-1);
            onDelete(roi.roiNumber, currentStructureSet?.dicomUid ?? 'default');
            setOpenedStructureDeletionConfirmationDialog(null);
        }
    };

    const handleDeleteStructureDialogCancel = () => {
        setOpenedStructureDeletionConfirmationDialog(null);
    };

    const quickApplyClearContoursOnSliceOperation = () => {
        if (environment && mainView) {
            const selectedStructureName = roi.name;
            const planeCoordinates = mainView.position[2];
            environment.clearContourOnSlice(
                selectedStructureName,
                planeCoordinates
            );
        }
    };

    return (
        <ConfirmationDialog
            open={openedStructureDeletionConfirmationDialog === roi.roiNumber}
            onCancel={handleDeleteStructureDialogCancel}
            onConfirm={handleDeleteStructureConfirmation}
            title={<FormattedMessage id="structureDeletion" />}
            message={
                <FormattedMessage
                    id="structureDeletionConfirmation"
                    values={{
                        structureName: <strong>{roi.name}</strong>,
                    }}
                />
            }
            cancelText={<FormattedMessage id="cancel" />}
            confirmText={<FormattedMessage id="delete" />}
        />
    );
};

export default StructureDeletionConfirmationDialog;
